export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export function getToken(): string | null {
  return localStorage.getItem('miraal_token') || localStorage.getItem('miraal_auth_token');
}

export function setToken(token: string): void {
  localStorage.setItem('miraal_token', token);
}

export function clearAuth(): void {
  localStorage.removeItem('miraal_token');
  localStorage.removeItem('miraal_auth_token');
  localStorage.removeItem('miraal_refresh');
  localStorage.removeItem('miraal_user');
  window.location.href = '/auth';
}

export function getHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function getSessions() {
  const res = await fetch(`${API_BASE_URL}/api/v1/chat/sessions`, {
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get sessions failed: ${res.status}`);
  return res.json();
}

/**
 * Full session detail, including every message's real, persisted
 * attachments (chat_message_attachments, joined server-side - see
 * get_session() in main.py). getSessions() above only returns session
 * metadata (id/title/updatedAt); this is the one authoritative source for
 * message content + attachments, used to hydrate a session from Postgres
 * rather than trusting the localStorage cache alone.
 */
export async function getSession(sessionId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${sessionId}`, {
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get session failed: ${res.status}`);
  return res.json();
}

export async function createSession(title?: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/chat/sessions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ title: title || 'New Chat' }),
  });

  if (res.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Create session failed: ${res.status} ${text}`);
  }

  return res.json();
}

/**
 * Non-streaming message. Returns full JSON response.
 */
export async function sendMessage(
  sessionId: string,
  text: string,
  options?: { model?: string; agent_type?: string; files?: string[] }
) {
  const model = options?.model || 'auto';
  const res = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      message: text,
      model,
      agent_type: options?.agent_type || null,
      stream: true,
      files: options?.files,
    }),
  });

  if (res.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(`Backend ${res.status}: ${errText}`);
  }

  return res.json();
}

export async function getMe() {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Auth check failed: ${res.status}`);
  return res.json();
}

/**
 * Streaming message. Reads SSE from FastAPI and calls your callbacks.
 */
export async function sendMessageStream(
  sessionId: string,
  text: string,
  options: { model?: string; agent_type?: string; signal?: AbortSignal; files?: string[] } | undefined,
  onToken: (token: string) => void,
  onDone: (meta: { modelUsed?: string; provider?: string; liveSearch?: boolean; messageId?: string }) => void,
  onStatus?: (status: string) => void
) {
  const model = options?.model || 'auto';
  const res = await fetch(`${API_BASE_URL}/api/v1/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      message: text,
      model,
      agent_type: options?.agent_type || null,
      stream: true,
      files: options?.files,
      plugins: JSON.parse(localStorage.getItem('miraal_plugins') || 'null'),
    }),
    signal: options?.signal,
  });

  if (res.status === 401) {
    clearAuth();
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok || !res.body) {
    const errText = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(`Backend ${res.status}: ${errText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let doneCalled = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (!jsonStr || jsonStr === '[DONE]') continue;

        try {
          const data = JSON.parse(jsonStr);

          if (data.token && typeof data.token === 'string') {
            onToken(data.token);
          }

          if (data.status && onStatus) {
            onStatus(data.status);
          }

          if (data.done === true && !doneCalled) {
            doneCalled = true;
            onDone({
              modelUsed: data.modelUsed || data.model_used,
              provider: data.provider,
              liveSearch: data.liveSearch || data.live_search,
              messageId: data.messageId || data.message_id,
            });
          }

          if (data.error) {
            const err = new Error(data.error) as Error & { code?: string };
            err.code = data.code;
            throw err;
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Unexpected end of JSON input') {
            throw e;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ============================================================================
// FILE UPLOAD
// ============================================================================
//
// Backend contract this assumes (adjust the path/field names below to match
// your actual FastAPI route once it exists):
//
//   POST {API_BASE_URL}/api/v1/files
//   Headers: Authorization: Bearer <token>   (Content-Type is set by the
//            browser automatically for multipart/form-data — do NOT set it
//            manually or the boundary will be wrong)
//   Body: multipart/form-data with a single field "file"
//
//   200 response (any of these shapes is accepted):
//     { "id": "file_abc123", "url": "https://.../file_abc123" }
//     { "data": { "id": "file_abc123", "url": "..." } }
//     { "file_id": "file_abc123", "file_url": "..." }
//
//   The returned `id` is what gets passed back into sendMessage /
//   sendMessageStream as an entry in `files: string[]`.
//
// Uses XMLHttpRequest instead of fetch because fetch has no native
// upload-progress event — XHR's `upload.onprogress` is what powers the
// progress % shown in the attachment chips.

export interface UploadedFile {
  id: string;
  url: string;
  name: string;
  type: string;
  size: number;
}

export function uploadFile(
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }

    const token = getToken();
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/v1/files`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    // NOTE: deliberately not setting Content-Type — the browser sets the
    // correct multipart/form-data boundary automatically for FormData.

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        clearAuth();
        reject(new Error('Session expired. Please log in again.'));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText || ''}`.trim()));
        return;
      }
      try {
        const data = JSON.parse(xhr.responseText);
        const id = data.id || data.file_id || data.data?.id;
        const url = data.url || data.file_url || data.data?.url || '';
        if (!id) {
          reject(new Error('Upload succeeded but the server did not return a file id'));
          return;
        }
        resolve({ id, url, name: file.name, type: file.type, size: file.size });
      } catch {
        reject(new Error('Upload succeeded but the response was not valid JSON'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }

    const formData = new FormData();
    formData.append('file', file);
    xhr.send(formData);
  });
}

// ============================================================================
// SWARM API FUNCTIONS (Connected to FastAPI Backend)
// ============================================================================
export async function createSwarmTask(query: string, agentCount = 10, projectId?: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ task: query, options: { agent_count: agentCount }, project_id: projectId || null }),
  });

  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Create swarm failed: ${res.status} - ${err}`);
  }
  return res.json();
}

export async function executeSwarmTask(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/execute`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Execute swarm failed: ${res.status}`);
  return res.json();
}

export async function getSwarmStatus(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/status`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Get status failed: ${res.status}`);
  return res.json();
}

export async function getSwarmResult(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/result`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Get result failed: ${res.status}`);
  return res.json();
}

/**
 * Phase 5 - the real durable execution log (swarm_execution_events), for
 * reconnect/refresh catch-up. `since` is the last sequence already
 * applied (0 = full history). Returns events strictly after it, in real
 * chronological/sequence order, plus the swarm's current event_seq.
 */
export async function getSwarmEvents(taskId: string, since = 0) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/events?since=${since}`, {
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Get swarm events failed: ${res.status}`);
  return res.json();
}

export async function cancelSwarmTask(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Cancel swarm failed: ${res.status}`);
  return res.json();
}

/**
 * Branded Piper/SYSPIN TTS (see NARRATION_VOICE_BY_LANG in main.py). Resolves
 * to a WAV Blob, or null on 204 (no branded voice installed for that
 * language) / any network failure - callers fall back to the browser's
 * Web Speech API on null, mirroring the pattern already used for swarm
 * narration in useAgentSwarm.ts.
 */
export async function narrateText(text: string, language: string, signal?: AbortSignal): Promise<Blob | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/swarm/narrate`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ text, language }),
      signal,
    });
    if (!res.ok || res.status === 204) return null;
    return res.blob();
  } catch {
    return null;
  }
}

export async function askSwarm(
  taskId: string,
  question: string,
  language: string,
): Promise<{ question: string; answer: string; language: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/ask`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ question, language }),
  });
  if (!res.ok) throw new Error(`Ask swarm failed: ${res.status}`);
  return res.json();
}

export async function listAgentPersonas() {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`List agents failed: ${res.status}`);
  return res.json();
}

export async function getAgentRoleInfo() {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/roles`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get agent role info failed: ${res.status}`);
  return res.json();
}

export async function getSwarmHistory(limit = 20) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/history?limit=${limit}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get swarm history failed: ${res.status}`);
  return res.json();
}

export interface MiraalProject {
  id: string;
  name: string;
  outputType: string;
  updatedAt: string;
  lastRun: { id: string; status: string; query: string } | null;
}

export async function getProjects(): Promise<{ projects: MiraalProject[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/projects`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get projects failed: ${res.status}`);
  return res.json();
}

export async function answerSwarmQuestion(taskId: string, answers: string[]) {
  const res = await fetch(`${API_BASE_URL}/api/v1/agents/swarm/${taskId}/answer`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
  if (!res.ok) throw new Error(`Answer swarm question failed: ${res.status}`);
  return res.json();
}

export interface ModelUsageEntry {
  model: string;
  calls: number;
  tokens: number;
  avgLatency: number;
  costEstimate: number;
}

export async function getModelUsage(): Promise<{ models: ModelUsageEntry[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/dash/models`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get model usage failed: ${res.status}`);
  return res.json();
}

export interface ModelDeployment {
  alias: string;
  realModel: string;
  agent: string;
  inCooldown: boolean;
}

export interface ModelHealth {
  totalDeployments: number;
  healthyCount: number;
  inCooldownCount: number;
  cooldownTimeSeconds: number;
  proactiveHealthChecksEnabled: boolean;
  deployments: ModelDeployment[];
}

export async function getModelHealth(): Promise<ModelHealth> {
  const res = await fetch(`${API_BASE_URL}/api/v1/dash/model-health`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get model health failed: ${res.status}`);
  return res.json();
}

export interface SpendAlert {
  id: string;
  plan: string;
  thresholdPct: number;
  spendAtTrigger: number;
  limitAtTrigger: number;
  month: string;
  createdAt: string;
}

export async function getSpendAlerts(): Promise<{ alerts: SpendAlert[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/alerts/spend`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Get spend alerts failed: ${res.status}`);
  return res.json();
}

export async function acknowledgeSpendAlert(alertId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/alerts/spend/${alertId}/acknowledge`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Acknowledge alert failed: ${res.status}`);
}


export async function listScheduledTasks() {
  const res = await fetch(`${API_BASE_URL}/api/v1/scheduled-tasks`, {
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`List scheduled tasks failed: ${res.status} - ${err}`);
  }
  return res.json();
}

export interface CreateScheduledTaskParams {
  name: string;
  task_description: string;
  frequency: 'daily' | 'weekly' | 'every_n_days';
  time_of_day?: string;
  day_of_week?: number;
  interval_days?: number;
}

export async function createScheduledTask(params: CreateScheduledTaskParams) {
  const res = await fetch(`${API_BASE_URL}/api/v1/scheduled-tasks`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Create scheduled task failed: ${res.status} - ${err}`);
  }
  return res.json();
}

export async function toggleScheduledTask(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/scheduled-tasks/${taskId}/toggle`, {
    method: 'PATCH',
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Toggle scheduled task failed: ${res.status} - ${err}`);
  }
  return res.json();
}

export async function deleteScheduledTask(taskId: string) {
  const res = await fetch(`${API_BASE_URL}/api/v1/scheduled-tasks/${taskId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Delete scheduled task failed: ${res.status} - ${err}`);
  }
  return res.json();
}


export interface CodeSession {
  id: string;
  name: string;
  language: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CodeSessionFile {
  path: string;
  content: string;
  updatedAt: string;
}

export interface CodeSessionTurn {
  id: string;
  instruction: string;
  explanation: string | null;
  commands: string[];
  attempts: number;
  exitCode: number | null;
  stdout: string | null;
  stderr: string | null;
  files: string[];
  success: boolean;
  createdAt: string;
  qualityNotes: string | null;
  snapshotCount: number;
}

export interface CodeSessionDetail extends CodeSession {
  files: CodeSessionFile[];
  turns: CodeSessionTurn[];
}

export async function createCodeSession(name: string, language: string = 'python'): Promise<CodeSession> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ name, language }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Create code session failed: ${res.status}`);
  return res.json();
}

export async function listCodeSessions(): Promise<{ sessions: CodeSession[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`List code sessions failed: ${res.status}`);
  return res.json();
}

export async function getCodeSession(sessionId: string): Promise<CodeSessionDetail> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get code session failed: ${res.status}`);
  return res.json();
}

export async function createCodeTurn(sessionId: string, instruction: string, planStages?: string[]): Promise<{ taskId: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/turns`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ instruction, plan_stages: planStages ?? null }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Create code turn failed: ${res.status}`);
  return res.json();
}

export interface CodeTurnPlan {
  task_type: 'feature' | 'bugfix' | 'refactor' | 'test' | 'other';
  stages: string[];
  needs_clarification: boolean;
  questions: string[];
  degraded: boolean;
}

// Pre-step before createCodeTurn: a short "here's what I'll build" preview
// the user confirms or cancels. Starts no work on its own.
export async function planCodeTurn(sessionId: string, instruction: string): Promise<CodeTurnPlan> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/plan`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ instruction }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Plan code turn failed: ${res.status}`);
  return res.json();
}

export async function uploadCodeFiles(sessionId: string, files: File[], paths: string[]): Promise<{ uploaded: string[]; failed: { path: string; reason: string }[] }> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  formData.append('paths', JSON.stringify(paths));
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/files`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Upload failed: ${res.status} - ${err}`);
  }
  return res.json();
}

// The file contents as they were after a given turn (lightweight version history).
export async function getTurnFiles(sessionId: string, turnId: string): Promise<{ files: { path: string; content: string }[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/turns/${turnId}/files`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get turn files failed: ${res.status}`);
  return res.json();
}

// Restore the session's files to a past turn's snapshot (overwrites those paths only).
export async function restoreTurnFiles(sessionId: string, turnId: string): Promise<{ restored: string[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/turns/${turnId}/restore`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Restore failed: ${res.status}`);
  return res.json();
}

// Phase D: a distinguishable error type for a real 409 (stale expected_hash)
// - the ONLY reliable way the editor's save flow can tell "the write was
// rejected because someone else changed the file" apart from every other
// failure (network, 503, 413, ...), each of which must preserve local
// content differently (see Step 15's table).
export class CodeConflictError extends Error {
  currentHash: string | null;
  constructor(message: string, currentHash: string | null) {
    super(message);
    this.name = 'CodeConflictError';
    this.currentHash = currentHash;
  }
}

export class CodeSaveError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'CodeSaveError';
    this.status = status;
  }
}

export async function updateCodeFile(sessionId: string, path: string, content: string, expectedHash?: string | null): Promise<CodeSessionFile & { contentHash: string; size: number }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/files/${encodeURIComponent(path)}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ content, expected_hash: expectedHash ?? null }),
    });
  } catch (networkErr: any) {
    // A real transport failure (offline, DNS, timeout) - the caller must
    // treat this exactly like any other failed save: preserve local
    // content and dirty state, never assume the write happened.
    throw new CodeSaveError(networkErr?.message || 'Network error - the file was not saved', 0);
  }
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    throw new CodeConflictError(
      body?.detail?.message || 'This file changed since it was last read - reload it before saving again',
      body?.detail?.current_hash ?? null,
    );
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const detail = typeof body?.detail === 'string' ? body.detail : JSON.stringify(body?.detail ?? {});
    const messages: Record<number, string> = {
      400: `Invalid request: ${detail || 'bad path or content'}`,
      404: 'File no longer exists in the workspace.',
      413: 'File exceeds the size limit.',
      422: `Request rejected: ${detail || 'unprocessable'}`,
      503: 'Workspace temporarily unavailable - please retry.',
    };
    throw new CodeSaveError(messages[res.status] || `Save failed: ${res.status}${detail ? ` - ${detail}` : ''}`, res.status);
  }
  return res.json();
}

// ─── Phase C: real project ingestion + complete file explorer ───

export interface CodeTreeNode {
  name: string;
  path: string;
  type: 'file' | 'folder';
  size?: number;
  updated_at?: number;
  children?: CodeTreeNode[];
}

export async function getCodeTree(sessionId: string): Promise<{ workspaceStatus: string; tree: CodeTreeNode[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/tree`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get tree failed: ${res.status}`);
  return res.json();
}

export class CodeFileNotFoundError extends Error {
  constructor(path: string) { super(`${path} not found`); this.name = 'CodeFileNotFoundError'; }
}
export class CodeFileUnsupportedError extends Error {
  constructor(detail: string) { super(detail); this.name = 'CodeFileUnsupportedError'; }
}

export async function readCodeFile(sessionId: string, path: string): Promise<{ path: string; content: string; contentHash: string; updatedAt: number; size: number }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/files/${encodeURIComponent(path)}`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 404) throw new CodeFileNotFoundError(path);
  if (res.status === 400) {
    const body = await res.json().catch(() => null);
    const detail = typeof body?.detail === 'string' ? body.detail : '';
    // Phase B/C's sandbox-runner returns this exact message for a real
    // UnicodeDecodeError - the one reliable signal that this is a binary/
    // non-text file, not some other 400.
    if (detail.includes('is not a text file')) throw new CodeFileUnsupportedError(detail);
    throw new Error(detail || `Read file failed: ${res.status}`);
  }
  if (res.status === 503) throw new Error('Workspace temporarily unavailable - please retry.');
  if (!res.ok) throw new Error(`Read file failed: ${res.status}`);
  return res.json();
}

export async function createCodeFile(sessionId: string, path: string, content: string = ''): Promise<{ path: string; contentHash: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/files/${encodeURIComponent(path)}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ content }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 409) throw new Error(`${path} already exists`);
  if (!res.ok) throw new Error(`Create file failed: ${res.status}`);
  return res.json();
}

export async function deleteCodeFile(sessionId: string, path: string): Promise<{ deleted: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/files/${encodeURIComponent(path)}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Delete file failed: ${res.status}`);
  return res.json();
}

export async function createCodeDirectory(sessionId: string, path: string): Promise<{ path: string; created: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/directories`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ path }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 409) throw new Error(`${path} already exists`);
  if (!res.ok) throw new Error(`Create folder failed: ${res.status}`);
  return res.json();
}

export async function deleteCodeDirectory(sessionId: string, path: string): Promise<{ deleted: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/directories/${encodeURIComponent(path)}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Delete folder failed: ${res.status}`);
  return res.json();
}

export async function renameCodePath(sessionId: string, fromPath: string, toPath: string): Promise<{ from_path: string; to_path: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/rename`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ from_path: fromPath, to_path: toPath }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 404) throw new Error(`${fromPath} not found`);
  if (res.status === 409) throw new Error(`${toPath} already exists`);
  if (!res.ok) throw new Error(`Rename failed: ${res.status}`);
  return res.json();
}

export async function searchCodeSession(sessionId: string, query: string): Promise<{ results: string[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/search?q=${encodeURIComponent(query)}`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

export async function uploadCodeZip(sessionId: string, file: File): Promise<{ uploaded: string[]; failed: { path: string; reason: string }[] }> {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/upload-zip`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    const violations = err?.detail?.violations ? `: ${err.detail.violations.join('; ')}` : '';
    throw new Error(`ZIP upload failed: ${res.status}${violations}`);
  }
  return res.json();
}

export async function getCodeTaskStatus(taskId: string): Promise<{ state: string; result?: any; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/tasks/${taskId}/status`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get code task status failed: ${res.status}`);
  return res.json();
}

export interface CodeTaskEvent {
  phase: 'connected' | 'writing' | 'testing' | 'revising' | 'linting' | 'done';
  attempt?: number;
  max_attempts?: number;
  success?: boolean;
  attempts?: number;
  explanation?: string | null;
  exit_code?: number | null;
  error?: string;
}

// Streams a code turn's live progress via SSE (fetch + ReadableStream, same
// pattern as streamChatCompletion). Resolves when the stream ends - either a
// terminal 'done' event or the connection closing. Throws on transport
// failure so the caller can fall back to getCodeTaskStatus polling. Cancel
// via opts.signal.
export async function streamCodeTask(
  taskId: string,
  onEvent: (e: CodeTaskEvent) => void,
  opts?: { signal?: AbortSignal },
): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}/api/v1/code/tasks/${taskId}/stream`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal: opts?.signal,
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok || !res.body) throw new Error(`Stream failed: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';
    for (const frame of frames) {
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue; // ": keepalive" comment frames
      try {
        onEvent(JSON.parse(dataLine.slice(6)) as CodeTaskEvent);
      } catch { /* malformed frame - ignore */ }
    }
  }
}

// ---- Phase E: real terminal + process manager ----

export interface CodeTerminalProcess {
  id: string;
  command: string;
  cwd: string;
  status: 'starting' | 'running' | 'stopping' | 'exited' | 'failed' | 'killed';
  exitCode: number | null;
  pid: number | null;
  startedAt: string;
  finishedAt: string | null;
  runtimeInstanceId?: string | null;
  observedAt?: string | null;
  requestedAction?: 'none' | 'stop' | 'kill' | 'restart' | 'cleanup';
}

export interface CodeTerminal {
  id: string;
  label: string;
  status: string;
  rows: number;
  cols: number;
  createdAt: string;
  lastActiveAt: string;
  closedAt: string | null;
  runtimeInstanceId?: string | null;
  observedAt?: string | null;
  requestedAction?: 'none' | 'stop' | 'kill' | 'restart' | 'cleanup';
  process: CodeTerminalProcess | null;
}

export async function createCodeTerminal(sessionId: string, label = 'Terminal', rows = 24, cols = 80): Promise<CodeTerminal> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ label, rows, cols }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 503) throw new Error('Workspace temporarily unavailable - please retry.');
  if (!res.ok) throw new Error(`Create terminal failed: ${res.status}`);
  return res.json();
}

export async function listCodeTerminals(sessionId: string): Promise<{ terminals: CodeTerminal[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`List terminals failed: ${res.status}`);
  return res.json();
}

export async function getCodeTerminal(sessionId: string, terminalId: string): Promise<CodeTerminal> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (res.status === 404) throw new Error('Terminal not found');
  if (!res.ok) throw new Error(`Get terminal failed: ${res.status}`);
  return res.json();
}

export async function stopCodeTerminal(sessionId: string, terminalId: string): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}/stop`, { method: 'POST', headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Stop terminal failed: ${res.status}`);
  return res.json();
}

export async function killCodeTerminal(sessionId: string, terminalId: string): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}/kill`, { method: 'POST', headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Kill terminal failed: ${res.status}`);
  return res.json();
}

export async function restartCodeTerminal(sessionId: string, terminalId: string): Promise<CodeTerminal> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}/restart`, { method: 'POST', headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Restart terminal failed: ${res.status}`);
  return res.json();
}

export async function resizeCodeTerminal(sessionId: string, terminalId: string, rows: number, cols: number): Promise<{ rows: number; cols: number }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}/resize`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ rows, cols }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Resize terminal failed: ${res.status}`);
  return res.json();
}

export async function deleteCodeTerminal(sessionId: string, terminalId: string): Promise<{ deleted: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}`, { method: 'DELETE', headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Delete terminal failed: ${res.status}`);
  return res.json();
}

// The JWT travels as a query param - browsers cannot set a custom
// Authorization header during a WebSocket handshake. sinceSeq resumes a
// reconnect from the real sandbox-runner output ring buffer instead of
// replaying nothing / everything.
export function getCodeTerminalStreamUrl(sessionId: string, terminalId: string, sinceSeq?: number): string {
  const wsBase = API_BASE_URL.replace(/^http/, 'ws');
  const token = getToken() || '';
  const since = typeof sinceSeq === 'number' ? `&since_seq=${sinceSeq}` : '';
  return `${wsBase}/api/v1/code/sessions/${sessionId}/terminals/${terminalId}/stream?token=${encodeURIComponent(token)}${since}`;
}

// ---- Billing ----
export interface BillingTier {
  label: string;
  river: string;
  priceInrMonth: number;
  monthlyCredits: number;
  monthlySpendCapInr: number;
  premiumAgents: string[];
  buildDepth: number;
  maxRevisions: number;
  maxConcurrentSwarms: number;
  outputBundles: string[];
  // True once this tier has a real Razorpay plan_id configured server-side -
  // Subscribe should open Razorpay Checkout instead of the free self-serve switch.
  requiresPayment: boolean;
}

export async function getBillingTiers(): Promise<{ tiers: Record<string, BillingTier> }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/billing/tiers`);
  if (!res.ok) throw new Error(`Get tiers failed: ${res.status}`);
  return res.json();
}

export async function getBillingMe(): Promise<{
  plan: string; label: string; river: string; priceInrMonth: number; monthlyCredits: number;
  credits: number; monthlySpendInr: number; monthlyCapInr: number; pctUsed: number;
  activeSwarms: number; maxConcurrentSwarms: number; buildDepth: number; maxRevisions: number;
  outputBundles: string[];
}> {
  const res = await fetch(`${API_BASE_URL}/api/v1/billing/me`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get billing failed: ${res.status}`);
  return res.json();
}

export async function changeBillingPlan(plan: string): Promise<{ success: boolean; plan: string; credits: number | null; message?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/billing/plan`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ plan }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Change plan failed: ${res.status}`);
  return res.json();
}

// Real recurring billing (Razorpay Subscriptions - not one-time Orders).
// Used for any tier where BillingTier.requiresPayment is true.
export async function subscribeBillingPlan(plan: string): Promise<{
  subscriptionId: string; keyId: string; plan: string; planLabel: string; planRiver: string;
  priceInrMonth: number; prefill: { name: string; email: string; contact: string };
}> {
  const res = await fetch(`${API_BASE_URL}/api/v1/billing/subscribe`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ plan }),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Subscribe failed: ${res.status}`);
  return res.json();
}

// Fast client-side confirmation only, right after Razorpay Checkout closes -
// NOT the source of truth for granting credits (the subscription.charged
// webhook is). Just confirms the signature so the UI can say "activating...".
export async function verifyBillingPayment(body: {
  razorpay_payment_id: string; razorpay_subscription_id: string; razorpay_signature: string;
}): Promise<{ verified: boolean }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/billing/verify-payment`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Verify failed: ${res.status}`);
  return res.json();
}

// ---- User preferences ----
export async function getUserPreferences(): Promise<{ preferences: {
  preferredTone?: string; preferredResponseLength?: string; autoSummarizeThreshold?: number;
  smartModelRouting?: boolean; autoArchiveThreshold?: number; customInstructions?: string;
  displayName?: string; nickname?: string; workType?: string;
  responseStyle?: 'default' | 'concise' | 'explanatory' | 'formal';
} }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/user/preferences`, { headers: getHeaders() });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Get preferences failed: ${res.status}`);
  return res.json();
}

export async function updateUserPreferences(body: {
  preferred_tone?: string; preferred_response_length?: string; auto_summarize_threshold?: number;
  smart_model_routing?: boolean; auto_archive_threshold?: number; custom_instructions?: string;
  display_name?: string; nickname?: string; work_type?: string;
  response_style?: 'default' | 'concise' | 'explanatory' | 'formal';
}): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/user/preferences`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 401) { clearAuth(); throw new Error('Session expired.'); }
  if (!res.ok) throw new Error(`Update preferences failed: ${res.status}`);
  return res.json();
}

export const apiClient = {
  sessions: { getSessions, getSession, createSession, sendMessage, sendMessageStream },
  auth: { getToken, setToken, clearAuth, getMe },
  scheduledTasks: { listScheduledTasks, createScheduledTask, toggleScheduledTask, deleteScheduledTask },
  code: {
    createCodeSession, listCodeSessions, getCodeSession, createCodeTurn, planCodeTurn, getCodeTaskStatus, streamCodeTask,
    uploadCodeFiles, updateCodeFile, getTurnFiles, restoreTurnFiles,
    getCodeTree, readCodeFile, createCodeFile, deleteCodeFile, createCodeDirectory, deleteCodeDirectory,
    renameCodePath, searchCodeSession, uploadCodeZip,
    createCodeTerminal, listCodeTerminals, getCodeTerminal, stopCodeTerminal, killCodeTerminal,
    restartCodeTerminal, resizeCodeTerminal, deleteCodeTerminal, getCodeTerminalStreamUrl,
  },
  swarm: {
    createSwarmTask,
    executeSwarmTask,
    getSwarmStatus,
    getSwarmResult,
    getSwarmEvents,
    cancelSwarmTask,
    askSwarm,
    listAgentPersonas,
    getAgentRoleInfo,
    getSwarmHistory,
    answerSwarmQuestion,
    getProjects,
  },
  dashboard: { getModelUsage, getModelHealth },
  files: { upload: uploadFile },
  billing: {
    getTiers: getBillingTiers, getMe: getBillingMe, changePlan: changeBillingPlan,
    subscribe: subscribeBillingPlan, verifyPayment: verifyBillingPayment,
  },
  preferences: { get: getUserPreferences, update: updateUserPreferences },
};

export async function chatWithGroq(
  messages: Array<{ role: string; content: string }>,
  model = 'groq-llama-3.1-8b'
) {
  const response = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ model, messages }),
  });
  if (!response.ok) {
    throw new Error(`Chat failed: ${response.status}`);
  }
  const data = await response.json();
  return data.choices[0].message.content;
}
